import { Tooltip } from 'antd';
import { useContext } from 'react';
import { CommonContext } from '../context/commonContext';

type Props = {
  goToNextPage: () => Promise<void>;
  goToPage: (newPage: number) => Promise<void>;
  goToPreviousPage: () => Promise<void>;
  currentPage: number;
  disableBackButton: () => boolean;
  disableNextButton: () => boolean;
  pagesToRender: number[];
  totalElements?: number;
  pageSize?: number;
  paginationTooltipTitle?: string;
};

export function Pagination({
  goToNextPage,
  goToPage,
  goToPreviousPage,
  currentPage,
  disableBackButton,
  disableNextButton,
  pagesToRender,
  totalElements,
  pageSize,
  paginationTooltipTitle,
}: Readonly<Props>) {
  const { disableNavigation, setModalWarningOpen } = useContext(CommonContext);
  return (
    <nav className="flex items-center justify-center gap-[10px]">
      {totalElements && pageSize && (
        <div>
          <p>
            {Math.min(currentPage * pageSize, totalElements)} out of{' '}
            {totalElements}
          </p>
        </div>
      )}

      <Tooltip title={disableNavigation ? paginationTooltipTitle : ''}>
        <ul className="flex items-center justify-center -space-x-px h-8 text-sm my-3">
          <li>
            <button
              type="button"
              disabled={disableBackButton()}
              className={`flex items-center justify-center px-3 h-8 ms-0 leading-tight text-gray-500 bg-white border border-e-0 border-gray-300 rounded-s-lg hover:bg-gray-100 hover:text-gray-700 ${
                disableBackButton()
                  ? 'cursor-default opacity-50 hover:bg-white hover:text-gray-500'
                  : ''
              }`}
              onClick={e => {
                if (disableNavigation) {
                  e.preventDefault();
                  setModalWarningOpen(true);
                } else {
                  goToPreviousPage();
                }
              }}
              data-testid="back-button"
            >
              <span className="sr-only">Previous</span>
              <svg
                className="w-2.5 h-2.5 rtl:rotate-180"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 6 10"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 1 1 5l4 4"
                />
              </svg>
            </button>
          </li>
          {!pagesToRender.length && (
            <li>
              <button
                type="button"
                className="flex items-center justify-center px-3 h-8 leading-tight z-10 text-blue-600 bg-blue-50 border border-gray-300 hover:bg-gray-100 hover:text-gray-700"
              >
                1
              </button>
            </li>
          )}
          {pagesToRender.map(page => (
            <li key={page}>
              <button
                type="button"
                onClick={e => {
                  if (disableNavigation) {
                    e.preventDefault();
                    setModalWarningOpen(true);
                  } else {
                    goToPage(page);
                  }
                }}
                className={`flex items-center justify-center px-3 h-8 leading-tight ${
                  page === currentPage
                    ? 'z-10 text-blue-600 bg-blue-50'
                    : 'text-gray-500 bg-white'
                } border border-gray-300 hover:bg-gray-100 hover:text-gray-700`}
              >
                {page}
              </button>
            </li>
          ))}
          <li>
            <button
              type="button"
              disabled={disableNextButton()}
              onClick={e => {
                if (disableNavigation) {
                  e.preventDefault();
                  setModalWarningOpen(true);
                } else {
                  goToNextPage();
                }
              }}
              className={`flex items-center justify-center px-3 h-8 leading-tight text-gray-500 bg-white border border-gray-300 rounded-e-lg hover:bg-gray-100 hover:text-gray-700 ${disableNextButton() ? 'cursor-default opacity-50 hover:bg-white hover:text-gray-500' : ''}`}
              data-testid="next-button"
            >
              <span className="sr-only">Next</span>
              <svg
                className="w-2.5 h-2.5 rtl:rotate-180"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 6 10"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 9 4-4-4-4"
                />
              </svg>
            </button>
          </li>
        </ul>
      </Tooltip>
    </nav>
  );
}
